import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import {
  FX_CURRENCIES,
  convertUnitPrice,
  findRate,
  rateLabel,
  vnDate,
  type CurrentPriceDto,
  type FxRate,
  type LastSaleDto,
  type ProductSummaryDto,
} from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/States";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { useProducts, useSettings } from "@/api/hooks";
import { useAuth } from "@/app/auth";
import { Select } from "@/components/ui/Select";
import { parseMoneyMajor } from "@/lib/moneyInput";
import { Link } from "react-router-dom";
import { formatUnitPrice, unitPriceToInput } from "@/domain/money";
import { QuotationProductImage } from "@/components/quotations/QuotationProductImage";
import type { LineDraft } from "./LineItemsEditor";

/** Alternative sale prices typed per product before it joins the grid. */
interface AltPrices {
  dtdPrice: string;
  exwPrice: string;
  underMoqPrice: string;
}

/**
 * Quy đổi đơn giá cho một sản phẩm đã chọn (yêu cầu v1.8 mục 6.4): tiền gốc + đơn giá gốc (nạp sẵn giá của khách / giá danh mục, sửa
 * được), tiền đích (phải là tiền của PI).
 */
interface FxDraft {
  source: string;
  price: string;
  target: string;
  loading: boolean;
  /** Người dùng đã tự sửa ô — giá của khách về muộn không được ghi đè số vừa gõ. */
  touched?: boolean;
  /** Không tải được giá riêng của khách: đang hiện giá danh mục, phải nói ra. */
  failed?: boolean;
}

type FxPreview =
  | { kind: "same"; minor: number }
  | { kind: "ok"; minor: number; exact: string; rate: FxRate }
  | { kind: "invalidPrice" }
  | { kind: "targetMismatch" }
  | { kind: "missingRate"; a: string; b: string };

function previewFx(fx: FxDraft, piCurrency: string | undefined, rates: readonly FxRate[]): FxPreview {
  const parsed = parseMoneyMajor(fx.price);
  if (parsed.invalid || parsed.minor === undefined) return { kind: "invalidPrice" };
  if (piCurrency && fx.target !== piCurrency) return { kind: "targetMismatch" };
  if (fx.source === fx.target) return { kind: "same", minor: parsed.minor };
  const result = convertUnitPrice(parsed.minor, fx.source, fx.target, rates);
  if (!result || !result.rate) return { kind: "missingRate", a: fx.source, b: fx.target };
  return { kind: "ok", minor: result.minor, exact: result.exact, rate: result.rate };
}

const blankPrices = (): AltPrices => ({
  dtdPrice: "",
  exwPrice: "",
  underMoqPrice: "",
});

const numToStr = (n: number | null | undefined) => (n != null ? String(n) : "");

/**
 * Turns a catalog product into a PI line, snapshotting the columns pi_items
 * keeps. Legacy defaults quantity to 1 and unit to "pieces".
 */
function toLine(p: ProductSummaryDto, prices: AltPrices): LineDraft {
  return {
    productId: p.id,
    productCode: p.productCode,
    subRef: p.subRef ?? "",
    customerStyleNo: "",
    description: p.description ?? "", descriptionZh: p.descriptionZh ?? "",
    color: p.color ?? "",
    size: p.size ?? "",
    unit: p.unit ?? "pieces",
    quantity: "1",
    moq: numToStr(p.moq),
    mcq: numToStr(p.mcq),
    msq: numToStr(p.msq),
    unitPrice: p.sellingPrice != null ? unitPriceToInput(p.sellingPrice) : "",
    imageUrl: p.imageUrl ?? null,
    ...prices,
  };
}

/**
 * Search the catalog, tick products, type their DTD / EXW / under-MOQ prices,
 * push them into the line grid. An overlay, not a route: it is a ten-second
 * sub-task of the editor page behind it.
 */
export function ProductPickerPanel({
  onAdd,
  onClose,
  customerId,
  currency,
  piId,
}: {
  customerId?: string;
  currency?: string;
  /** PI đang sửa — "lần mua gần nhất" không tính chính nó. */
  piId?: string;
  onAdd: (lines: LineDraft[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<
    { product: ProductSummaryDto; prices: AltPrices }[]
  >([]);
  // Quy đổi đơn giá (v1.8 mục 6.4): TẮT mặc định. Tắt = không quy đổi gì, giữ đúng hành vi cũ.
  const [convertOn, setConvertOn] = useState(false);
  const [fx, setFx] = useState<Record<string, FxDraft>>({});
  const settings = useSettings();
  const rates = settings.data?.businessOperations.fxRates ?? [];
  const { user } = useAuth();

  /** Nạp giá gốc để quy đổi: giá của khách nếu có, không thì giá danh mục — đúng nguồn mà nút Thêm vẫn dùng. */
  const seedFx = (product: ProductSummaryDto) => {
    setFx((prev) => ({
      ...prev,
      [product.id]: {
        source: product.currency,
        price: product.sellingPrice != null ? unitPriceToInput(product.sellingPrice) : "",
        target: currency ?? product.currency,
        loading: !!customerId,
      },
    }));
    if (!customerId) return;
    void apiFetch<{ data: CurrentPriceDto }>(`/products/${product.id}/price`, { query: { customerId } })
      .then(({ data }) =>
        setFx((prev) => {
          const cur = prev[product.id];
          if (!cur) return prev;
          if (cur.touched) return { ...prev, [product.id]: { ...cur, loading: false } };
          const resolved =
            data.source === "none"
              ? { source: cur.source, price: cur.price }
              : { source: data.currency, price: data.price != null ? unitPriceToInput(data.price) : cur.price };
          return { ...prev, [product.id]: { ...cur, ...resolved, loading: false } };
        }),
      )
      .catch(() => setFx((prev) => (prev[product.id] ? { ...prev, [product.id]: { ...prev[product.id]!, loading: false, failed: true } } : prev)));
  };
  const results = useProducts({
    q: q.trim() || undefined,
    page: 1,
    pageSize: 25,
  });

  const isPicked = (id: string) => picked.some((p) => p.product.id === id);
  const toggle = (product: ProductSummaryDto) => {
    if (convertOn && !isPicked(product.id)) seedFx(product);
    setPicked((prev) =>
      prev.some((p) => p.product.id === product.id)
        ? prev.filter((p) => p.product.id !== product.id)
        : [
            ...prev,
            {
              product,
              prices:
                product.currency === currency || !currency
                  ? {
                      dtdPrice:
                        product.dtdPrice == null
                          ? ""
                          : unitPriceToInput(product.dtdPrice),
                      exwPrice:
                        product.exwPrice == null
                          ? ""
                          : unitPriceToInput(product.exwPrice),
                      underMoqPrice:
                        product.underMoqPrice == null
                          ? ""
                          : unitPriceToInput(product.underMoqPrice),
                    }
                  : blankPrices(),
            },
          ],
    );
  };
  const setPrice = (id: string, patch: Partial<AltPrices>) =>
    setPicked((prev) =>
      prev.map((p) =>
        p.product.id === id ? { ...p, prices: { ...p.prices, ...patch } } : p,
      ),
    );

  const confirm = async () => {
    if (!picked.length || lock.current) return;
    // Quy đổi bật: mọi sản phẩm đã chọn phải có kết quả hợp lệ — thiếu tỷ giá, sai tiền đích, giá gốc không sạch thì dừng, không
    // thêm dòng nào nửa vời.
    if (convertOn) {
      // Giá riêng của khách chưa về thì chưa thêm: thêm lúc này là quy đổi từ giá danh mục mà không ai hay.
      if (picked.some(({ product }) => fx[product.id]?.loading)) {
        setError(t("fxConvert.waitPrice"));
        return;
      }
      for (const { product } of picked) {
        const draft = fx[product.id];
        const pv = draft ? previewFx(draft, currency, rates) : { kind: "invalidPrice" as const };
        if (pv.kind !== "ok" && pv.kind !== "same") {
          setError(t("fxConvert.stopped", { code: [product.productCode, product.subRef].filter(Boolean).join(" / ") }));
          return;
        }
      }
      lock.current = true;
      onAdd(
        picked.map(({ product, prices }) => {
          const draft = fx[product.id]!;
          const pv = previewFx(draft, currency, rates);
          const parsed = parseMoneyMajor(draft.price);
          const minor = pv.kind === "ok" || pv.kind === "same" ? pv.minor : 0;
          return {
            // Giá DTD / EXW / dưới MOQ: giữ đúng những gì đang có trên dòng chọn — sản phẩm khác tiền tệ vốn để trống, trừ khi người
            // dùng tự gõ (theo tiền của PI), như khi tắt quy đổi.
            ...toLine(product, prices),
            unitPrice: unitPriceToInput(minor),
            fxConversion:
              pv.kind === "ok" && parsed.minor !== undefined
                ? { sourceCurrency: draft.source as (typeof FX_CURRENCIES)[number], sourceUnitPrice: parsed.minor, base: pv.rate.base, quote: pv.rate.quote, rate: pv.rate.rate }
                : undefined,
          };
        }),
      );
      lock.current = false;
      onClose();
      return;
    }
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const lines = await Promise.all(
        picked.map(async ({ product, prices }) => {
          const { data: resolved } = await apiFetch<{ data: CurrentPriceDto }>(
            `/products/${product.id}/price`,
            { query: { customerId: customerId || undefined } },
          );
          const price =
            resolved.source === "none" ? product.sellingPrice : resolved.price;
          const sourceCurrency =
            resolved.source === "none" ? product.currency : resolved.currency;
          return {
            ...toLine(product, prices),
            unitPrice:
              (!currency || sourceCurrency === currency) && price != null
                ? unitPriceToInput(price)
                : "",
          };
        }),
      );
      onAdd(lines);
      onClose();
    } catch (err) {
      setError(getLocalizedError(err, t, "pi.errorLoad"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const rows = results.data?.data ?? [];

  // Lần mua gần nhất của khách cho các sản phẩm ĐANG HIỆN (yêu cầu v1.8 mục 4.6) — một yêu cầu cho cả trang kết quả, đổi khách
  // là đọc lại. Chỉ để tham khảo: không bao giờ tự ghi đè giá của PI.
  const shownIds = rows.map((p) => p.id);
  const lastSales = useQuery({
    queryKey: ["last-sales", customerId ?? "", piId ?? "", shownIds.join(",")],
    enabled: !!customerId && shownIds.length > 0,
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      apiFetch<{ data: LastSaleDto[] }>("/products/last-sales", {
        query: { customerId, productIds: shownIds.join(","), excludePiId: piId || undefined },
        signal,
      }).then((r) => new Map(r.data.map((x) => [x.productId, x]))),
  });

  return (
    <Modal
      eyebrow={t("pi.document")}
      title={t("pi.addProducts")}
      closeLabel={t("common.close")}
      onClose={() => {
        if (!busy) onClose();
      }}
      className="max-w-4xl"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4">
        <p className="text-xs text-ink-soft">
          {t("piWorkspace.priceHint", { currency })}
        </p>
        {error && (
          <p role="alert" className="text-danger text-xs">
            {error}
          </p>
        )}
        <Input
          className="shrink-0"
          data-autofocus="true"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("pi.searchProducts")}
          leading={<Search />}
        />

        <div className="max-h-64 shrink-0 overflow-auto overscroll-contain rounded-sm border border-line">
          {results.isLoading ? (
            <LoadingState />
          ) : results.error ? (<ErrorState message={t("pi.errorLoad")} onRetry={()=>void results.refetch()}/>) : rows.length === 0 ? (
            <EmptyState title={t("common.noMatches")} />
          ) : (
            <table className="w-full border-collapse text-xs">
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-line last:border-0">
                    <td className="w-8 px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => toggle(p)}
                        aria-pressed={isPicked(p.id)}
                        aria-label={[p.productCode, p.subRef]
                          .filter(Boolean)
                          .join(" / ")}
                        className={`flex size-5 items-center justify-center rounded-xs border ${
                          isPicked(p.id)
                            ? "border-brand bg-brand text-white"
                            : "border-line-strong text-transparent"
                        }`}
                      >
                        <Check className="size-3.5" />
                      </button>
                    </td>
                    <td className="w-12 px-1 py-1">
                      {/* Ảnh ngay trong kết quả tìm (yêu cầu v1.8 mục 4.5): nhận ra sản phẩm bằng mắt trước khi thêm vào PI. */}
                      <QuotationProductImage compact product={p} />
                    </td>
                    <td className="px-2 py-1.5 font-mono tabular text-ink">
                      {p.productCode}
                      {p.subRef ? ` / ${p.subRef}` : ""}
                    </td>
                    <td className="px-2 py-1.5 text-ink-soft">
                      {p.description}
                    </td>
                    <td className="px-2 py-1.5 text-ink-faint">
                      {[p.color, p.size].filter(Boolean).join(" · ")}
                    </td>
                    <td className="min-w-48 px-2 py-1.5 text-right align-top">
                      <div className="font-mono tabular text-ink-soft" title={t("piPicker.listPrice")}>
                        {p.currency}{" "}
                        {p.sellingPrice != null
                          ? unitPriceToInput(p.sellingPrice)
                          : "—"}
                      </div>
                      <LastSaleCell
                        customerId={customerId}
                        loading={lastSales.isLoading}
                        failed={lastSales.isError}
                        sale={lastSales.data?.get(p.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {picked.length > 0 && (
          <div className="flex shrink-0 flex-col gap-3">
            {picked.map(({ product, prices }) => (
              <div
                key={product.id}
                className="rounded-sm border border-line p-3"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-mono tabular text-xs text-ink">
                    {product.productCode} / {product.subRef} ·{" "}
                    {product.currency}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(product)}
                    aria-label={t("common.removeLine")}
                    className="text-ink-faint hover:text-danger"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <div>
                    <FieldLabel>{t("common.dtdPrice")}</FieldLabel>
                    <Input
                      inputMode="decimal"
                      value={prices.dtdPrice}
                      onChange={(e) =>
                        setPrice(product.id, { dtdPrice: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <FieldLabel>{t("common.exwPrice")}</FieldLabel>
                    <Input
                      inputMode="decimal"
                      value={prices.exwPrice}
                      onChange={(e) =>
                        setPrice(product.id, { exwPrice: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <FieldLabel>{t("common.underMoqPrice")}</FieldLabel>
                    <Input
                      inputMode="decimal"
                      value={prices.underMoqPrice}
                      onChange={(e) =>
                        setPrice(product.id, { underMoqPrice: e.target.value })
                      }
                    />
                  </div>
                </div>
                {convertOn && fx[product.id] && (
                  <FxBlock
                    draft={fx[product.id]!}
                    piCurrency={currency}
                    rates={rates}
                    canOpenSettings={user?.role === "admin"}
                    onChange={(patch) => setFx((prev) => ({ ...prev, [product.id]: { ...prev[product.id]!, ...patch, touched: true } }))}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line p-4">
        <label className="mr-auto flex max-w-xl items-start gap-2 text-xs text-ink-soft">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-[var(--color-brand)]"
            checked={convertOn}
            onChange={(e) => {
              const on = e.target.checked;
              setConvertOn(on);
              setError(null);
              if (on) for (const { product } of picked) if (!fx[product.id]) seedFx(product);
            }}
          />
          <span>
            <span className="font-medium text-ink">{t("fxConvert.toggle")}</span>
            <span className="block">{t("fxConvert.toggleHint", { currency })}</span>
          </span>
        </label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={busy}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={confirm}
          disabled={!picked.length || busy || (convertOn && picked.some(({ product }) => fx[product.id]?.loading))}
        >
          {t("pi.addToInvoice")}
        </Button>
      </div>
    </Modal>
  );
}

/** Lần mua gần nhất của khách cho một sản phẩm: ngày · số PI đủ · đơn giá + tiền tệ / đơn vị — cùng một dòng PI. */
function LastSaleCell({
  customerId,
  loading,
  failed,
  sale,
}: {
  customerId?: string;
  loading: boolean;
  failed: boolean;
  sale: LastSaleDto | undefined;
}) {
  const { t } = useTranslation();
  if (!customerId) return <div className="mt-0.5 text-2xs text-ink-faint">{t("piPicker.pickCustomer")}</div>;
  if (loading) return <div className="mt-0.5 text-2xs text-ink-faint">{t("common.loading")}</div>;
  if (failed) return <div className="mt-0.5 text-2xs text-danger">{t("piPicker.historyError")}</div>;
  if (!sale) return <div className="mt-0.5 text-2xs text-ink-faint">{t("piPicker.noHistory")}</div>;
  return (
    <div className="mt-0.5 text-2xs text-ink-soft" title={t("piPicker.lastSaleTitle")}>
      <span className="text-ink-faint">{t("piPicker.lastSale")}:</span>{" "}
      <span className="font-mono tabular">{sale.date ? vnDate(sale.date) : "—"}</span>{" "}
      · <span className="font-mono tabular">{t("piPicker.piNo", { no: sale.piNo })}</span>
      <div className="font-mono tabular font-medium text-ink">
        {sale.currency} {formatUnitPrice(sale.unitPrice, sale.currency, false)}
        {sale.unit ? <span className="font-normal text-ink-soft"> / {sale.unit}</span> : null}
      </div>
    </div>
  );
}

/** Khối quy đổi của một sản phẩm: tiền gốc · đơn giá gốc · tiền đích · tỷ giá · kết quả, xem trước trước khi áp. */
function FxBlock({
  draft,
  piCurrency,
  rates,
  canOpenSettings,
  onChange,
}: {
  draft: FxDraft;
  piCurrency?: string;
  rates: readonly FxRate[];
  canOpenSettings: boolean;
  onChange: (patch: Partial<FxDraft>) => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const pv = previewFx(draft, piCurrency, rates);
  const rate = draft.source !== draft.target ? findRate(rates, draft.source, draft.target) : null;
  return (
    <div className="mt-3 rounded-sm border border-dashed border-line-strong bg-sunken/40 p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <div>
          <FieldLabel htmlFor={`${uid}-src`}>{t("fxConvert.source")}</FieldLabel>
          <Select id={`${uid}-src`} containerClassName="w-full" value={draft.source} onChange={(e) => onChange({ source: e.target.value })}>
            {!(FX_CURRENCIES as readonly string[]).includes(draft.source) && <option value={draft.source}>{draft.source}</option>}
            {FX_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor={`${uid}-price`}>{t("fxConvert.sourcePrice")}</FieldLabel>
          <Input id={`${uid}-price`} inputMode="decimal" className="text-right font-mono" value={draft.price} onChange={(e) => onChange({ price: e.target.value })} />
          {draft.loading && <span className="text-2xs text-ink-faint">{t("fxConvert.loadingPrice")}</span>}
          {!draft.loading && draft.failed && !draft.touched && <span role="alert" className="text-2xs text-warn">{t("fxConvert.priceFailed")}</span>}
        </div>
        <div>
          <FieldLabel htmlFor={`${uid}-dst`}>{t("fxConvert.target")}</FieldLabel>
          <Select id={`${uid}-dst`} containerClassName="w-full" value={draft.target} onChange={(e) => onChange({ target: e.target.value })}>
            {FX_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        <div>
          <FieldLabel>{t("fxConvert.rate")}</FieldLabel>
          <p className="flex h-9 items-center font-mono text-xs text-ink">{rate ? rateLabel(rate) : "—"}</p>
        </div>
        <div>
          <FieldLabel>{t("fxConvert.result")}</FieldLabel>
          <p className="flex h-9 items-center justify-end font-mono tabular text-sm font-semibold text-ink">
            {pv.kind === "ok" || pv.kind === "same" ? `${draft.target} ${unitPriceToInput(pv.minor)}` : "—"}
          </p>
        </div>
      </div>
      <div className="mt-1 text-2xs">
        {pv.kind === "ok" && (
          <span className="text-ink-soft">
            {t("fxConvert.exact", { value: pv.exact })} · {t("fxConvert.precision")}
          </span>
        )}
        {pv.kind === "same" && <span className="text-ink-soft">{t("fxConvert.same")}</span>}
        {pv.kind === "invalidPrice" && <span role="alert" className="text-danger">{t("fxConvert.invalidPrice")}</span>}
        {pv.kind === "targetMismatch" && <span role="alert" className="text-danger">{t("fxConvert.targetMismatch", { currency: piCurrency })}</span>}
        {pv.kind === "missingRate" && (
          <span role="alert" className="text-danger">
            {t("fxConvert.missingRate", { a: pv.a, b: pv.b })}{" "}
            {canOpenSettings && <Link className="underline" to="/settings" target="_blank" rel="noreferrer">{t("fxConvert.openSettings")}</Link>}
          </span>
        )}
      </div>
    </div>
  );
}
